// ===========================================================================
// Activities in rooms (contract D0): the catalog and the frame's limits.
//
// An activity is a page in a sandboxed iframe inside the group screen. The
// server relays its state and events and never runs any of its rules, so
// every game here is replaceable and none of them can see anything the room
// does not hand it. What lives HERE is what both ends must agree on: which
// activities exist, who may run them, the field names the text policy looks
// at, and the sizes past which the server stops listening. ASCII only.
// ===========================================================================

import { TELEPHONE_MIN_PLAYERS } from './decks/telephone.js';

const BOTH_BANDS = Object.freeze(['adult', 'minor']);
const ALL_MODES = Object.freeze(['video', 'audio', 'text']);

/**
 * Every activity the picker may offer. Both bands get all six: the content is
 * the provider's own (the decks), the typed fields go through the text policy
 * for the room's band, and every canvas is sampled through the image
 * classifier like a camera frame. `modes` says which kinds of room may run it;
 * a drawing game needs no camera, so all three kinds may.
 */
export const ACTIVITY_CATALOG = Object.freeze([
  Object.freeze({
    slug: 'question-cards',
    name: 'Question cards',
    blurb: 'Would you rather, two truths, ask away. Vote without your name on it, then see the spread.',
    minPlayers: 1,
    bands: BOTH_BANDS,
    modes: ALL_MODES,
    needsCanvas: false,
  }),
  Object.freeze({
    slug: 'spectrum',
    name: 'Spectrum',
    blurb: 'A slider between two ends. One person sees the marker and gives one clue; everyone else guesses where it sits.',
    minPlayers: 2,
    bands: BOTH_BANDS,
    modes: ALL_MODES,
    needsCanvas: false,
  }),
  Object.freeze({
    slug: 'draw-and-guess',
    name: 'Draw and guess',
    blurb: 'One person draws a word, everyone else types guesses. Eighty seconds a round, and the drawer rotates.',
    minPlayers: 2,
    bands: BOTH_BANDS,
    modes: ALL_MODES,
    needsCanvas: true,
  }),
  Object.freeze({
    slug: 'trivia',
    name: 'Trivia',
    blurb: 'Ten quick questions with a scoreboard, or write a fake answer and see who you fool.',
    minPlayers: 1,
    bands: BOTH_BANDS,
    modes: ALL_MODES,
    needsCanvas: false,
  }),
  Object.freeze({
    slug: 'whiteboard',
    name: 'Whiteboard',
    blurb: 'A shared canvas with no rules. Draw together.',
    minPlayers: 1,
    bands: BOTH_BANDS,
    modes: ALL_MODES,
    needsCanvas: true,
  }),
  Object.freeze({
    slug: 'telephone',
    name: 'Telephone',
    blurb: 'Write a sentence, draw the one you are handed, describe the drawing you get. Then play the album.',
    minPlayers: TELEPHONE_MIN_PLAYERS,
    bands: BOTH_BANDS,
    modes: ALL_MODES,
    needsCanvas: true,
  }),
]);

/** The catalog entry for a slug, or undefined. */
export function activityBySlug(slug) {
  return ACTIVITY_CATALOG.find((a) => a.slug === slug);
}

/**
 * May this activity run in a room of this band and kind? Unknown slugs,
 * bands, and modes answer no: the server is the one asking, and a typo there
 * must refuse rather than open something to everyone.
 * @param {string} slug
 * @param {{ band?: string, mode?: string }} room
 */
export function activityAllowed(slug, { band, mode } = {}) {
  const a = activityBySlug(slug);
  if (!a) return false;
  if (!a.bands.includes(band)) return false;
  return a.modes.includes(mode || 'video');
}

/** The ops a client may send in ClientMsg.ACTIVITY. */
export const ActivityOp = Object.freeze({
  START: 'start',
  STOP: 'stop',
  SET: 'set',
  EVENT: 'event',
  FRAME: 'frame',
});

/**
 * The field names inside an activity's state or event data that carry words
 * a person typed. Wherever one of these appears -- however deeply nested --
 * its string value passes through the text policy for the room's band. A
 * page that wants free text moderated names the field from this list; a
 * field named anything else is treated as game data.
 */
export const ACTIVITY_TEXT_FIELDS = Object.freeze([
  'text', 'word', 'guess', 'answer', 'prompt', 'clue', 'caption', 'title',
]);

/** A `set` replaces the whole state, so this is the size of a whole game. */
export const ACTIVITY_STATE_MAX_BYTES = 64 * 1024;
/** One event. A stroke is a few hundred bytes; a telephone drawing is a small PNG. */
export const ACTIVITY_EVENT_MAX_BYTES = 128 * 1024;
/** A canvas sample: a PNG data URL, decoded. */
export const ACTIVITY_FRAME_MAX_BYTES = 300 * 1024;
/** How often a room's canvas is sampled, at most. The host page sends one on this clock. */
export const ACTIVITY_FRAME_INTERVAL_MS = 20_000;
/** Per-member budget for events. Strokes are the hot path; a hand draws well under this. */
export const ACTIVITY_EVENT_RATE = Object.freeze({ perSec: 10, burst: 20 });

/** How deep the text walk goes. Game state is shallow; a deeper shape is a bug or a probe. */
const TEXT_WALK_MAX_DEPTH = 8;

/**
 * Every typed string in a value: the string values of fields named in
 * ACTIVITY_TEXT_FIELDS, anywhere in nested objects and arrays.
 *
 * Numbers, booleans, and strings under other names are skipped without a
 * second look, which is what keeps a stroke -- an array of number pairs --
 * cheap to walk: nothing in it is a string, so nothing in it is classified.
 *
 * @param {unknown} value
 * @returns {Array<{ key: string, text: string }>}
 */
export function collectTextFields(value) {
  const out = [];
  const walk = (v, key, depth) => {
    if (depth > TEXT_WALK_MAX_DEPTH || v == null) return;
    if (typeof v === 'string') {
      if (key && ACTIVITY_TEXT_FIELDS.includes(key) && v.trim()) out.push({ key, text: v });
      return;
    }
    if (typeof v !== 'object') return;
    if (Array.isArray(v)) {
      // An array under a text name is a list of texts ("answers" is not a
      // name on the list, but "text": ["a", "b"] would be), so the key is
      // carried down to its string items.
      for (const item of v) walk(item, key, depth + 1);
      return;
    }
    for (const [k, item] of Object.entries(v)) walk(item, k, depth + 1);
  };
  walk(value, null, 0);
  return out;
}

export default {
  ACTIVITY_CATALOG,
  activityBySlug,
  activityAllowed,
  ActivityOp,
  ACTIVITY_TEXT_FIELDS,
  ACTIVITY_STATE_MAX_BYTES,
  ACTIVITY_EVENT_MAX_BYTES,
  ACTIVITY_FRAME_MAX_BYTES,
  ACTIVITY_FRAME_INTERVAL_MS,
  ACTIVITY_EVENT_RATE,
  collectTextFields,
};
